import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {
    ExtendedChartOptions,
    ExtendedData
} from '../../bb-charts/bb-chart/bb-chart.interfaces';
import { formatCurrency, formatDate, formatNumber, formatPercent } from '@angular/common';
import {
    addDays,
    differenceInDays,
    differenceInMonths,
    endOfDay,
    isEqual,
    isWithinInterval,
    subDays,
    subMinutes
} from 'date-fns';
import { BenchmarkService } from '../../state/benchmark/benchmark.service';
import { BenchmarkQuery } from '../../state/benchmark/benchmark.query';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    ObservedValueOf,
    of,
    ReplaySubject,
    Subject,
    switchMap
} from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    takeUntil,
    tap,
    throttleTime
} from 'rxjs/operators';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BenchmarkState } from '../../state/benchmark/benchmark.store';
import { fromCamelCaseToSentence } from '../../bb-charts/bb-chart/bb-chart-utils';
import { SbEventBusService } from '@services';
import { SbChart } from '../../bb-charts/bb-chart/bb-chart.component';
import { Moment, months } from 'moment';
import { UiQuery } from '../../state/ui/ui.query';
import { distinctUntilArrayItemChanged, filterNilValue } from '@datorama/akita';
import {
    arrayToDateMap,
    createDateRange,
    DateMap,
    getDeltaArray,
    getPeriods,
    getPeriodsMap,
    mapDataToRange,
    Period,
    RangeScale,
    setMaxCulling
} from '../../utils/chart-utils';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import { Data } from 'billboard.js';
import { Benchmark } from '../../state/benchmark/benchmark.model';
import { TooltipOptions } from 'billboard.js/types/options';
import { fadeInOut } from '../../animations/fade-in-out.animation';
import { Currency } from '../currency-selector/currency-country-codes';

@Component({
    selector: 'sb-performance-chart',
    templateUrl: './sb-performance-chart.component.html',
    styleUrls: ['./sb-performance-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [fadeInOut]
})
export class SbPerformanceChartComponent implements OnInit, OnDestroy {
    /**
     * Whether to default to the benchmarks saved in local storage
     */
    @Input() defaultBenchmarks = true;

    get axisType(): ObservedValueOf<typeof this._axisType$> {
        return this._axisType$.value;
    }

    @Input() set axisType(value: ObservedValueOf<typeof this._axisType$>) {
        if (value) {
            this._axisType$.next(value);
            this.chartRef?.flush(true);
        }
    }
    get error(): { title: string; content: string; type: 'critical' | 'warning' } {
        return this._error;
    }

    get events$(): Observable<ObservedValueOf<typeof this._events$>> {
        return this._events$.asObservable();
    }

    @Input() set events(value: ObservedValueOf<typeof this._events$>) {
        this._events$.next(value);
    }
    get usePreviousEndOfDay(): boolean {
        return this._usePreviousEndOfDay;
    }

    @Input() set usePreviousEndOfDay(value: boolean) {
        this._usePreviousEndOfDay = value;
    }
    get excludeWeekends$(): Observable<boolean> {
        return this._excludeWeekends$.asObservable();
    }

    @Input() set excludeWeekends(value: boolean) {
        this._excludeWeekends$.next(value);
    }
    get loading(): boolean {
        return this._loading;
    }

    @Input() set loading(value: boolean) {
        this._loading = value;
        this.loadingChange.emit(this.loading);
    }

    onLoadingStateChange(isLoading: boolean) {
        this.loading = isLoading;
        //this.loadingChange.emit(isLoading);
    }

    get customOptions(): ObservedValueOf<typeof this._customOptions$> {
        return this._customOptions$.value;
    }

    get customOptions$() {
        return this._customOptions$.asObservable();
    }

    @Input() set customOptions(value: ObservedValueOf<typeof this._customOptions$>) {
        this._customOptions$.next(value);
    }
    get period$(): Observable<Partial<Period>> {
        return this._period$.asObservable();
    }

    @Input() set period(value: Partial<Period>) {
        if (JSON.stringify(value) !== JSON.stringify(this._period$.value)) {
            if (value.start && value.end) {
                this.periodChange.emit(value);
            }
            this._period$.next(value);
        }
    }

    private _showLegendPercentages = true;
    @Input() set showLegendPercentages(value: boolean) {
        this._showLegendPercentages = value;
    }

    get showLegendPercentages() {
        return this._showLegendPercentages;
    }

    get chartRef(): SbChart {
        return this._chartRef;
    }

    set chartRef(value: SbChart) {
        this._chartRef = value;
    }
    get chartData(): ExtendedData {
        return this._chartData$.value;
    }

    set chartData(value: ExtendedData) {
        this._chartData$.next(<any>value);
    }

    minDate$: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
    maxDate$: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);

    @Input() set context(context: string) {
        this._context = context;
        switch (context) {
            case 'dashboard-performance-chart':
            case 'dashboard-market-value-chart':
            case 'portfolio-v2-performance-chart':
            case 'portfolio-v2-market-value-chart':
            case 'investment-performance-chart':
                break;
            case 'account-performance-chart':
            case 'performance-chart-filtered':
                this.showScaleSelector = true;
                this.showPeriodSelector = true;
                this.showBenchmarkSelector = true;
                break;
            case 'portfolio-report-performance-chart':
                this.showBenchmarkSelector = true;
                break;
            case 'marketdata-price-chart':
            default:
                break;
        }
    }
    get context(): string {
        return this._context;
    }
    private _context: string;

    @Input() set series(data) {
        if (!data) {
            return;
        }
        const values = data[0]?.data;
        if (values && values.length > 0) {
            this.minDate$.next(new Date(values[0][0]));
            this.maxDate$.next(new Date(values[values.length - 1][0]));
        }
        if (
            this.context === 'dashboard-market-value-chart' ||
            this.context === 'portfolio-v2-market-value-chart'
        ) {
            this.series$.next(arrayToDateMap(data, { marketValue: null }));
        }
        // TODO: refactor and remove hardcoded values and checks used for price chart
        else if (data[0]?.name === 'Price') {
            //this.loading = true;
            //this.showPeriodSelector = true;
            //this.showBenchmarkSelector = true;
            this.series$.next(
                arrayToDateMap(data, {
                    price: 0
                })
            );
            if (
                this.context !== 'account-performance-chart' &&
                this.context !== 'investment-performance-chart'
            ) {
                this.period = getPeriodsMap()['1Y'];
            }
        } else if (data.length > 0) {
            //this.loading = true;
            /*this.showScaleSelector = true;
            this.showPeriodSelector = true;
            this.showBenchmarkSelector = true;*/
            this.series$.next(
                arrayToDateMap(data, {
                    return: 0,
                    'returnExcl.FX': 1,
                    'FX Effect': 2
                })
            );
        } else {
            // TODO: add no data state
            this.series$.next({});
        }
    }

    @Output() periodChange = new EventEmitter();
    @Output() loadingChange = new EventEmitter<boolean>();

    constructor(
        private benchmarkService: BenchmarkService,
        private benchmarkQuery: BenchmarkQuery,
        private _userSettingsQuery: UserSettingsQuery,
        private _cdr: ChangeDetectorRef,
        private _sbEventBusService: SbEventBusService,
        private _fb: UntypedFormBuilder,
        public ui: UiQuery
    ) {
        this._sbEventBusService
            .getObservable('Print')
            .pipe(
                filter((event) => event && event.payload && event.payload === 'Preview'),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((event) => {
                this.chartRef?.resize({ height: 280 });
                this.renderBenchmarks();
            });

        this.reactiveForm = this._fb.group({
            fromDate: '',
            toDate: ''
        });

        this.reactiveForm.valueChanges
            .pipe(
                filter((dates) => dates.fromDate !== '' && dates.toDate !== ''),
                map((dates) => ({
                    start: (dates.fromDate as Moment)?.toDate(),
                    end: (dates.toDate as Moment)?.toDate()
                })),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((period) => {
                if (period.start <= period.end) {
                    this.period = {
                        name: 'Custom',
                        start: period.start,
                        end: period.end
                    };
                } else {
                    this.reactiveForm.get('toDate').setErrors({ invalidDate: true });
                }
            });
    }
    reactiveForm: UntypedFormGroup;
    unsubscribe$ = new Subject();
    benchmark = new FormControl<Array<Benchmark['id']>>([]);

    @Input() set benchmarks(benchmarks: Array<Benchmark['id']>) {
        if (benchmarks !== this.benchmark.value) {
            this.benchmark.setValue(benchmarks);
        }
    }

    @Output() benchmarksChange = new EventEmitter<Array<Benchmark['id']>>();

    private _loading = false;
    showDateNotice = false;
    showMaxDateNotice = false;
    showError = false;
    private _error: { title: string; content: string; type: 'critical' | 'warning' };
    private _chartRef: SbChart;
    private _usePreviousEndOfDay = false;
    private _events$ = new BehaviorSubject({ eventBought: {}, eventSold: {} });

    // Today for max date
    today = new Date();

    renderBenchmarksTimeout: any;

    ngOnInit() {
        this.renderBenchmarksTimeout = setTimeout(() => {
            this.renderBenchmarks();
        }, 2000);
    }

    ngOnDestroy() {
        clearTimeout(this.renderBenchmarksTimeout);
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    // Today or ToDate as max for From datepicker
    getMaxFromDate(): Date {
        const toDateValue = this.reactiveForm.get('toDate').value;
        return toDateValue ? toDateValue : this.today;
    }

    benchmarks$ = this.benchmark.valueChanges.pipe(
        throttleTime(400, undefined, { leading: true, trailing: true }),
        tap((benchmarks) => this.benchmarksChange.emit(benchmarks)),
        switchMap((benchmarks) => {
            if (this.defaultBenchmarks) {
                localStorage.setItem(this.context, JSON.stringify(benchmarks));
            }
            return benchmarks.length === 0
                ? of({})
                : combineLatest(
                      benchmarks.map((benchmark: string) =>
                          this.benchmarkService
                              .get(benchmark)
                              .pipe(
                                  switchMap((res) =>
                                      this.benchmarkQuery.selectBenchmark$(benchmark)
                                  )
                              )
                      )
                  ).pipe(
                      map((benchmarks: Array<any>) => {
                          return benchmarks.reduce(
                              (acc: {}, benchmark: BenchmarkState) => {
                                  if (!benchmark || !benchmark.id) {
                                      return acc;
                                  }
                                  acc[benchmark.id] =
                                      this.benchmarkService.fillGapsInBenchmarkData(
                                          benchmark.data
                                      );
                                  return acc;
                              },
                              {}
                          );
                      })
                  );
        }),
        shareReplay(1),
        takeUntil(this.unsubscribe$)
    );

    renderBenchmarks() {
        const benchmarks = JSON.parse(localStorage.getItem(this.context)) || [];
        if (this.defaultBenchmarks) {
            this.benchmark.setValue(benchmarks);
        }
    }

    benchmarkOptions$ = this.benchmarkQuery.selectAvailableBenchmarks$;
    showPeriodSelector = false;
    showBenchmarkSelector = false;
    showScaleSelector = false;
    scale = [
        {
            name: 'Daily',
            value: 'endOfDay'
        },
        {
            name: 'Weekly',
            value: 'endOfWeek'
        },
        {
            name: 'Monthly',
            value: 'endOfMonth'
        }
    ];
    periods: Array<Period> = getPeriods();
    private _period$ = new BehaviorSubject<Partial<Period>>({
        ...getPeriodsMap().YTD
    });
    scale$: BehaviorSubject<RangeScale> = new BehaviorSubject('endOfDay');
    private _chartData$: BehaviorSubject<ExtendedData> = new BehaviorSubject({});
    series$: ReplaySubject<DateMap> = new ReplaySubject<DateMap>(1);
    private _excludeWeekends$ = new BehaviorSubject<boolean>(false);
    private _axisType$ = new BehaviorSubject({ type: 'relative' });

    range$ = combineLatest([
        this._period$.pipe(filter((period) => !!period.start && !!period.end)),
        this.scale$.pipe(),
        this.minDate$.pipe(
            filterNilValue(),
            distinctUntilChanged((prev, curr) => prev === curr)
        ),
        this.maxDate$.pipe(
            filterNilValue(),
            distinctUntilChanged((prev, curr) => prev === curr)
        ),
        this.excludeWeekends$
    ]).pipe(
        debounceTime(50),
        map(([period, scale, minDate, maxDate, excludeWeekends]) => {
            // Filter out invalid maxDate.
            if (period.end < minDate) {
                return [];
            }

            // if requested period is before minDate (first data point we have), set start to minDate
            let startDate = period.start;
            if (period.start < minDate && period.context !== 'portfolio-report') {
                startDate = minDate;
                this.showDateNotice = true;
            } else {
                this.showDateNotice = false;
            }

            // if requested period is after maxDate (last data point we have), set end to maxDate
            if (period.context === 'portfolio-report') {
                this.showMaxDateNotice = false;
                period.end = maxDate;
            } else if (period.end > maxDate) {
                if (
                    period.end >
                    new Date(
                        subMinutes(
                            endOfDay(addDays(maxDate, 1)),
                            new Date().getTimezoneOffset()
                        )
                    )
                ) {
                    this.showMaxDateNotice = true;
                }
                period.end = maxDate;
            }

            if (period.name === 'YTD' || period.name === 'MTD') {
                startDate = subDays(startDate, 1);
            }

            // if startDate is less than period.end, create a data range, otherwise return an empty array.
            return startDate < period.end
                ? createDateRange(startDate, period.end, scale, excludeWeekends)
                : [];
        }),
        distinctUntilArrayItemChanged(),
        shareReplay(1)
    );

    private _customOptions$ = new BehaviorSubject<{
        resetChart?: boolean;
        grid?: {
            hideGridlines?: boolean;
        };
        legend?: {
            position?: 'top-left';
        };
        tooltip?: TooltipOptions;
        currency?: Currency;
    }>({});

    private _axisFormatter = {
        relative: (x, locale, initialPrice, currency) =>
            formatPercent(+x, locale, '0.0-2'),
        absolute: (x, locale, initialPrice, currency) => {
            if (this.context === 'investment-performance-chart') {
                return formatCurrency(
                    (+x + 1) * initialPrice,
                    locale,
                    currency,
                    'symbol',
                    '1.0-0'
                );
            }
            return formatCurrency(x, locale, currency, 'symbol', '1.0-0');
        }
    };

    private _initialValue: number = 0;

    chartConfig$ = combineLatest([
        this.range$,
        this.series$,
        this.benchmarks$,
        this.events$,
        this._userSettingsQuery.selectGlobalSetting$('LOCALE'),
        this._period$,
        this.customOptions$
    ]).pipe(
        debounceTime(100),
        distinctUntilChanged((prev, curr) => {
            const unchanged = JSON.stringify(prev) === JSON.stringify(curr);
            if (unchanged) {
                this.onLoadingStateChange(false);
            }
            return unchanged;
        }),
        map(([range, series, benchmarks, _events, locale, period, customOptions]) => {
            let bought = [];
            let sold = [];
            if (
                _events &&
                (Object.keys(_events.eventBought || {}).length > 0 ||
                    Object.keys(_events.eventSold || {}).length > 0)
            ) {
                const eventDates = [
                    ...Object.keys(_events.eventBought || {}),
                    ...Object.keys(_events.eventSold || {})
                ].filter((value, index, array) => {
                    return (
                        array.indexOf(value) === index &&
                        isWithinInterval(new Date(+value), {
                            start: period?.start,
                            end: period?.end
                        })
                    );
                });
                range = [...range, ...eventDates.map((date) => new Date(+date))].sort(
                    (a, b) => new Date(a).getTime() - new Date(b).getTime()
                );
            }
            const columns = Object.keys(series).reduce((acc, key, index) => {
                const rangeData = mapDataToRange(series[key], range);
                const delta = getDeltaArray(rangeData);
                if (period.name === 'YTD' || period.name === 'MTD') {
                    delta.shift();
                    rangeData.shift();
                }
                if (
                    _events &&
                    (Object.keys(_events.eventBought || {}).length > 0 ||
                        Object.keys(_events.eventSold || {}).length > 0)
                ) {
                    const eventData = _events;
                    const adjustment =
                        period.name === 'YTD' || period.name === 'MTD' ? -1 : 0;
                    if (Object.keys(_events.eventBought || {}).length > 0) {
                        bought = delta.map((value, index) => {
                            const date = new Date(range[index - adjustment]).getTime();
                            return eventData.eventBought[date]
                                ? value || getLastKnownValue(delta, index, 1) || 0
                                : null;
                        });
                    }
                    if (Object.keys(_events.eventSold || {}).length > 0) {
                        sold = delta.map((value, index) => {
                            const date = range[index - adjustment]?.getTime();
                            return eventData.eventSold[date]
                                ? value || getLastKnownValue(delta, index, 1) || 0
                                : null;
                        });
                    }
                }

                return [...acc, [key, ...delta], [`${key}_value`, ...rangeData]];
            }, []);
            const benchmark = Object.keys(benchmarks)?.reduce((acc, key) => {
                const rangeData = mapDataToRange(benchmarks[key], range);
                const delta = getDeltaArray(rangeData);
                if (period.name === 'YTD' || period.name === 'MTD') {
                    delta.shift();
                    rangeData.shift();
                }
                return [
                    ...acc,
                    [`${key}_benchmark_`, ...delta],
                    [`${key}_value`, ...rangeData]
                ];
            }, []);
            if (period.name === 'YTD' || period.name === 'MTD') {
                range.shift();
            }
            const hide = [
                ...columns
                    .map((data) => data[0])
                    .filter((name) => name.indexOf('_value') !== -1),
                ...benchmark
                    .map((data) => data[0])
                    .filter((name) => name.indexOf('_value') !== -1)
            ];

            const months = differenceInMonths(period.end, period.start);
            const maxCullingFormat = setMaxCulling(period, months, range);
            const data = [['x', ...range], ...columns, ...benchmark];
            if (bought.length > 0) {
                data.push(['eventBought', ...bought]);
            }
            if (sold.length > 0) {
                data.push(['eventSold', ...sold]);
            }
            const pattern = data.map((serie) => {
                if (serie[0] === 'eventBought') {
                    return (
                        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="icon icon-tabler icons-tabler-filled icon-tabler-circle-plus">\n' +
                        '  <path stroke="none" d="M6 6h12v12H6z" fill="none" />\n' +
                        '  <path d="M4.929 4.929a10 10 0 1 1 14.141 14.141a10 10 0 0 1 -14.14 -14.14zm8.071 4.071a1 1 0 1 0 -2 0v2h-2a1 1 0 1 0 0 2h2v2a1 1 0 1 0 2 0v-2h2a1 1 0 1 0 0 -2h-2v-2z" />\n' +
                        '</svg>'
                    );
                }
                if (serie[0] === 'eventSold') {
                    return (
                        '<svg xmlns="http://www.w3.org/2000/svg"  width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="icon icon-tabler icons-tabler-filled icon-tabler-circle-minus">\n' +
                        '   <path stroke="none" d="M3 6h12v12H6z" fill="none" />\n' +
                        '   <path d="M2.81,3.02c3.86-3.95,10.19-4.03,14.14-.18,3.95,3.86,4.03,10.19.18,14.14-.06.06-.12.12-.18.18-3.97,3.84-10.3,3.73-14.14-.25C-.94,13.04-.94,6.9,2.81,3.02h0ZM6.88,9.09c-.55,0-1,.45-1,1s.45,1,1,1h6c.55,0,1-.45,1-1s-.45-1-1-1h-6Z"/>\n' +
                        '</svg>'
                    );
                }
                return 'circle';
            });
            pattern.shift();
            this._initialValue = [
                ...(data.find(
                    (serie) =>
                        serie[0] === 'price_value' || serie[0] === 'marketValue_value'
                ) || [])
            ].find((x, index) => x !== null && index > 0);

            const chartConfig: { data: Data; options: ExtendedChartOptions } = {
                data: {
                    x: 'x',
                    columns: data
                },
                options: {
                    resetChart: customOptions?.resetChart ?? false,
                    data: {
                        types: {
                            marketValue: 'area-spline', // for ESM specify as: area()
                            price: 'area-spline', // for ESM specify as: area()
                            return: 'area-spline', // for ESM specify as: area()
                            'returnExcl.FX': 'spline', // for ESM specify as: area()
                            ...Object.keys(benchmarks).reduce(
                                (acc, benchmark) => ({
                                    ...acc,
                                    [`${benchmark}_benchmark_`]: 'spline'
                                }),
                                {}
                            ),
                            eventBought: 'line',
                            eventSold: 'line'
                        },
                        hide: [...hide, 'returnExcl.FX', 'FX Effect'],
                        axes: {
                            marketValue_value: 'y2'
                        }
                    },
                    legend: {
                        hide,
                        showPercentages: this._showLegendPercentages
                    },
                    area: {
                        below: true,
                        zerobased: false
                    },
                    spline: {
                        interpolation: {
                            type: 'monotone-x'
                        }
                    },
                    line: {
                        connectNull: true
                    },
                    point: {
                        focus: {
                            only: false
                        },
                        pattern
                    },
                    axis: {
                        x: {
                            localtime: true,
                            type: 'timeseries',
                            tick: {
                                fit: true,
                                multiline: false,
                                autorotate: true,
                                rotate:
                                    this.ui.viewportSize === 'xs' ||
                                    this.ui.viewportSize === 'sm'
                                        ? 60
                                        : 0,
                                culling: {
                                    max: maxCullingFormat.maxCulling,
                                    lines: false
                                },
                                //centered: true,
                                //count: 7,
                                format: (x) => {
                                    // skip first and last value
                                    if (
                                        isEqual(x, range[0]) ||
                                        isEqual(x, range[range.length - 1])
                                    ) {
                                        return '';
                                    }
                                    return formatDate(x, maxCullingFormat.format, locale);
                                }
                            },
                            clipPath: false
                        },
                        y: {
                            show: true,
                            inner: true,
                            tick: {
                                format: () => ''
                            }
                        },
                        y2: {
                            inner: true,
                            show: true,
                            tick: {
                                format: (x) => {
                                    return this._axisFormatter[this.axisType.type](
                                        x,
                                        locale,
                                        this._initialValue,
                                        this.customOptions?.currency
                                    );
                                },
                                culling: {
                                    max: 3
                                }
                            }
                        }
                    },
                    tooltip: {
                        //doNotHide: true,
                        contents: function (this, data) {
                            const series = data.reduce((acc, serie, index) => {
                                const serieIndex =
                                    this.data().findIndex((s) => s.id === serie.name) + 1;
                                const color = `--bb-chart-serie-${serieIndex}-color-1`;
                                const benchmarks = this.data().reduce((acc, serie) => {
                                    if (serie.id.indexOf('_benchmark_') !== -1) {
                                        acc[serie.id] = serie.id;
                                    }
                                    return acc;
                                }, {});
                                const isBenchmark =
                                    serie.name.indexOf('_benchmark_') !== -1;
                                let classNames = '';
                                if (isBenchmark) {
                                    const benchmarkIndex =
                                        Object.keys(benchmarks).findIndex(
                                            (benchmark) =>
                                                serie.name.indexOf(benchmark) !== -1
                                        ) + 1;
                                    classNames = ` benchmark-color benchmark-color-${benchmarkIndex}`;
                                }

                                let html = `<div class="column${classNames}" style="--color: var(${color})">
            <dt>${fromCamelCaseToSentence(serie.name.replace(/_benchmark_/, ''))}</dt>
            <dd><strong>${formatPercent(serie.value, locale, '0.0-2')}</strong>
                <!--(${formatNumber(
                    this.data()[serieIndex]?.values[data[0].index].value,
                    locale,
                    '0.0-2'
                )})--></dd>
        </div>`;
                                return acc + html;
                            }, '');
                            return `
<figure class="bb-tooltip bb-tooltip-date">
    <figcaption>${formatDate(data[0].x, 'fullDate', 'en-SE')}</figcaption>
    <dl>
    ${series}
    </dl>
</figure>
`;
                        }
                    }
                }
            };
            if (customOptions?.grid?.hideGridlines) {
                chartConfig.options.grid = {
                    ...chartConfig.options.grid,
                    x: { show: false },
                    y: { show: false }
                };
            }
            if (customOptions?.legend?.position === 'top-left') {
                chartConfig.options.legend.position = 'top-left';
            }

            if (customOptions?.tooltip) {
                chartConfig.options.tooltip = customOptions.tooltip;
            }
            return chartConfig;
        }),
        shareReplay(1)
    );

    isSelected(o1, o2) {
        return o1.name === o2;
    }
    setPeriod(period: Partial<Period>) {
        if (!period.end && !period.start) {
            period = getPeriodsMap()[period.name];
        } else if (period.name === 'Custom' && !period.context) {
            period.start = this.reactiveForm.value.fromDate?.toDate
                ? this.reactiveForm.value.fromDate?.toDate()
                : null;
            period.end = this.reactiveForm.value.toDate?.toDate
                ? this.reactiveForm.value.toDate?.toDate()
                : null;
        } else if (period.name === 'All') {
            period.start = period.start || this.minDate$.getValue();
            period.end = period.end || this.maxDate$.getValue();
        }
        if (differenceInDays(period.end, period.start) > 360 * 5) {
            this.setScale('endOfMonth');
        } else if (differenceInDays(period.end, period.start) > 360 * 3) {
            this.setScale('endOfWeek');
        } else if (
            (period.name !== 'All' && period.name !== 'Custom') ||
            period.context === 'portfolio-report'
        ) {
            this.setScale('endOfDay');
        }
        this.period = { ...period };
    }

    setScale(scale: RangeScale) {
        this.scale$.next(scale);
    }

    /*toggleDisplayValue() {
        this.chartRef.show('priceValue');
        this.chartRef.hide('price');
        this.chartRef.config('axis.y2.tick.format', (x) => x, true);
    }*/
    get fitHeightToParent(): boolean {
        return this._fitHeightToParent;
    }

    @Input() set fitHeightToParent(value: boolean) {
        this._fitHeightToParent = value;
    }
    private _fitHeightToParent: boolean = false;

    @Input() setError(error: typeof this._error) {
        this.loading = false;
        this.showError = true;
        this._error = error;
    }

    clearError() {
        this.showError = false;
        this._error = null;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes?.series?.currentValue?.length > 0 && this.context) {
            if (
                this.context === 'portfolio-report-performance-chart' ||
                this.context === 'marketdata-price-chart'
            ) {
                this.period = {
                    name: 'All',
                    start: this.minDate$.getValue(),
                    end: this.maxDate$.getValue()
                };
            }
        }
    }
}

const getLastKnownValue = (data: Array<any>, index: number, modifier = 0) => {
    if (index < 0) {
        return null;
    }
    return data[index - modifier] ?? getLastKnownValue(data, index - modifier, 1);
};
